import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtFecha, initials, toLocalISO } from "@/lib/api";
import { calcularSaldoVacaciones, calcularPeriodosDetallados, fechasEnRango, diasEntre } from "@/lib/vacaciones";
import { generarComprobanteVacacionesPagadas } from "@/lib/vacacionesPagadasPDF";
import { Palmtree, Plus, X, Trash2, Calendar, FileDown, FileText, Pencil } from "lucide-react";
import ReportModal from "@/components/ReportModal";
import { reporteVacaciones } from "@/lib/reportes";
import { toast } from "sonner";

export default function Vacaciones() {
  const [empleados, setEmpleados] = useState([]);
  const [asistencia, setAsistencia] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [empresa, setEmpresa] = useState({});
  const [editingGrupo, setEditingGrupo] = useState(null);

  const cargar = async () => {
    const [e, a] = await Promise.all([
      api.get("/empleados"),
      api.get("/asistencia"),
    ]);
    setEmpleados(e.data || []);
    setAsistencia(a.data || []);
    try { const { data: emp } = await api.get("/empresa"); setEmpresa(emp || {}); } catch (err) { console.error("Vacaciones: no se pudo cargar empresa", err); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Saldos por empleado: fusiona `calcularSaldoVacaciones` (vencidas, aniversario)
  // con `calcularPeriodosDetallados` (overrides + rebajos + arrastres) para que
  // la tabla de /vacaciones muestre EXACTAMENTE los mismos números que la pestaña
  // Vacaciones del modal del empleado.
  const saldos = useMemo(() => empleados
    .filter(e => e.estado === "Activo")
    .map(emp => {
      const base = calcularSaldoVacaciones(emp, asistencia, new Date(), empresa);
      const detalle = calcularPeriodosDetallados(emp, asistencia, new Date(), empresa);
      // "Período Actual" en la tabla = último período ya CERRADO (donde se generaron los
      // días completos). Si aún no hay ningún período cerrado (empleado con <1 año),
      // usar el en curso. "Período Nuevo" siempre es el próximo/en curso.
      const nP = detalle.periodos.length;
      const ultimo = nP > 0 ? detalle.periodos[nP - 1] : null; // en curso normalmente
      const ultimoCerrado = [...detalle.periodos].reverse().find(p => !p.esActual) || ultimo;
      const enCurso = detalle.periodos.find(p => p.esActual);
      // Disfrutados del "actual" (último cerrado, o en curso si no hay cerrado).
      const disfrutadosPeriodoActual = ultimoCerrado ? (ultimoCerrado.disfrutados || 0) : 0;
      // "Período Nuevo" = período en curso si existe y difiere del "actual"; si no, próximo proyectado.
      let labelProximo = null;
      let diasProximo = 0;
      if (enCurso && ultimoCerrado && enCurso.label !== ultimoCerrado.label) {
        labelProximo = enCurso.label;
        diasProximo = enCurso.generados; // días acumulados en el en curso
      } else if (ultimo && ultimo.label) {
        // Proyectar el próximo (aún no ha empezado)
        const parts = ultimo.label.split("-");
        if (parts.length === 2) {
          const y1 = parseInt(parts[0], 10);
          const y2 = parseInt(parts[1], 10);
          if (!isNaN(y1) && !isNaN(y2)) labelProximo = `${y1 + 1}-${y2 + 1}`;
        }
      }
      return {
        emp,
        ...base,
        disfrutados: disfrutadosPeriodoActual,
        saldo: detalle.saldoTotal,
        diasVencidas: detalle.saldoVencidas,
        diasPeriodoActual: ultimoCerrado ? ultimoCerrado.generados : 0,
        diasPeriodoNuevo: diasProximo,
        labelPeriodoActual: ultimoCerrado ? ultimoCerrado.label : null,
        labelPeriodoNuevo: labelProximo,
      };
    }),
    [empleados, asistencia, empresa]);

  // Vacaciones tomadas o registradas (estado "Vacaciones" en asistencia), ordenadas por fecha
  const registros = useMemo(() => {
    const sorted = asistencia
      .filter(a => a.estado === "Vacaciones")
      .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
    // Pagadas: cada registro es su propio grupo con dias = dias_pagados (o 1 legacy).
    // Disfrutadas: agrupar rangos contiguos.
    const grupos = [];
    for (const r of sorted) {
      const tipo = r.tipo_vacacion || "Disfrutadas";
      if (tipo === "Pagadas") {
        const dias = Number(r.dias_pagados) > 0 ? Number(r.dias_pagados) : 1;
        grupos.push({
          empleado_id: r.empleado_id,
          fecha_inicio: r.fecha,
          fecha_fin: r.fecha,
          dias,
          tipo,
          asistencia_id: r.id,
        });
        continue;
      }
      const lastGroup = grupos.find(g =>
        g.empleado_id === r.empleado_id &&
        g.tipo === tipo &&
        plusOneDay(g.fecha_fin) === r.fecha
      );
      if (lastGroup) {
        lastGroup.fecha_fin = r.fecha;
        lastGroup.dias++;
      } else {
        grupos.push({ empleado_id: r.empleado_id, fecha_inicio: r.fecha, fecha_fin: r.fecha, dias: 1, tipo });
      }
    }
    return grupos.reverse();
  }, [asistencia]);

  const descargarComprobantePagadas = (g) => {
    const rec = asistencia.find(a => a.id === g.asistencia_id);
    const emp = empleados.find(x => x.id === g.empleado_id);
    if (!rec || !emp) { toast.error("No se encontró el registro"); return; }
    try {
      const result = generarComprobanteVacacionesPagadas({
        empresa,
        empleado: emp,
        fechaPago: rec.fecha,
        diasPagados: Number(rec.dias_pagados) || 1,
        monto: Number(rec.monto_pagado) || 0,
        motivo: rec.motivo || "",
      });
      // Re-archivar (upsert idempotente): garantiza que el comprobante siga en el expediente.
      api.post("/comprobantes", {
        empleado_id: emp.id,
        periodo_label: `Vacaciones Pagadas ${fmtFecha(rec.fecha)}`,
        kind: "vacaciones_pagadas",
        fecha_inicio: rec.fecha,
        fecha_fin: rec.fecha,
        monto_bruto: Number(rec.monto_pagado) || 0,
        monto_ccss: 0,
        monto_rebajos: 0,
        monto_neto: Number(rec.monto_pagado) || 0,
        file_name: result.fileName,
        pdf_base64: result.pdfBase64,
      }).catch(err => console.error("No se pudo re-archivar el comprobante:", err));
    } catch (err) {
      toast.error("No se pudo generar el comprobante: " + (err?.message || ""));
    }
  };

  const eliminarRango = async (g) => {
    const confirmMsg = g.tipo === "Pagadas"
      ? `¿Eliminar el pago de vacaciones del ${fmtFecha(g.fecha_inicio)} (${g.dias} ${g.dias === 1 ? "día" : "días"})?`
      : `¿Eliminar las vacaciones del ${fmtFecha(g.fecha_inicio)} al ${fmtFecha(g.fecha_fin)}?`;
    if (!window.confirm(confirmMsg)) return;
    if (g.tipo === "Pagadas" && g.asistencia_id) {
      await api.delete(`/asistencia/${g.asistencia_id}`).catch(() => {});
    } else {
      // Buscar todos los registros del rango con estado Vacaciones para ese empleado
      const fechas = fechasEnRango(g.fecha_inicio, g.fecha_fin);
      const toDelete = asistencia.filter(a => a.empleado_id === g.empleado_id && a.estado === "Vacaciones" && (a.tipo_vacacion || "Disfrutadas") !== "Pagadas" && fechas.includes(a.fecha));
      await Promise.all(toDelete.map(r => api.delete(`/asistencia/${r.id}`).catch(() => {})));
    }
    await cargar();
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><Palmtree size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Vacaciones</h1>
            <p className="text-sm text-secondary-c">Saldo acumulado y registro de días disfrutados por empleado.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReport(true)} className="np-btn np-btn-outline" data-testid="vacaciones-report-btn"><FileDown size={16} className="mr-1.5" />Reporte</button>
          <button onClick={() => setShowForm(true)} className="np-btn np-btn-primary inline-flex items-center gap-2" data-testid="open-vacacion-form">
            <Plus size={16} /> Registrar vacaciones
          </button>
        </div>
      </header>

      {showReport && (
        <ReportModal
          title="Reporte de Vacaciones" mode="simple"
          onClose={() => setShowReport(false)}
          onGenerate={({ format }) => reporteVacaciones({
            format, empresa,
            rows: saldos.map(s => ({
              empleado: `${s.emp.nombre} ${s.emp.apellidos || ""}`.trim(),
              ingreso: s.emp.fecha_ingreso, antiguedad: s.periodoActual,
              generadas: s.generados, tomadas: s.disfrutados,
              saldo: s.saldo, porVencer: s.diasPorVencer || 0,
            })),
          })}
        />
      )}

      <div className="np-card p-0 np-scroll mb-6">
        <table className="np-table text-sm">
          <thead><tr>
            <th>Empleado</th>
            <th>Fecha Ingreso</th>
            <th className="text-center">Días Generados en Periodo Actual</th>
            <th className="text-center">Días Disfrutados</th>
            <th className="text-center">Período Nuevo</th>
            <th className="text-center">Saldo</th>
            <th className="text-center">Días Vencidos de Periodos anteriores</th>
            <th>Próximo aniversario</th>
          </tr></thead>
          <tbody>
            {saldos.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-secondary-c">No hay empleados activos.</td></tr>
            )}
            {saldos.map(({ emp, disfrutados, saldo, proximoAniversario, diasPeriodoActual, diasPeriodoNuevo, diasVencidas, labelPeriodoActual, labelPeriodoNuevo }) => (
              <tr key={emp.id} data-testid={`vac-row-${emp.cedula}`}>
                <td>
                  <div className="flex items-center gap-3">
                    {emp.avatar
                      ? <img src={emp.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                      : <div className="w-9 h-9 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-xs font-semibold">{initials(`${emp.nombre} ${emp.apellidos}`)}</div>}
                    <div>
                      <div className="font-medium">{emp.nombre} {emp.apellidos}</div>
                      <div className="text-xs text-secondary-c">{emp.puesto} · {emp.departamento}</div>
                    </div>
                  </div>
                </td>
                <td>{emp.fecha_ingreso ? fmtFecha(emp.fecha_ingreso) : "—"}</td>
                <td className="text-center">
                  {labelPeriodoActual ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-secondary-c">{labelPeriodoActual}</div>
                      <div className="font-medium">{diasPeriodoActual} {diasPeriodoActual === 1 ? "día" : "días"}</div>
                    </div>
                  ) : "—"}
                </td>
                <td className="text-center text-secondary-c">{disfrutados}</td>
                <td className="text-center">
                  {labelPeriodoNuevo ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-secondary-c">{labelPeriodoNuevo}</div>
                      <div className="font-medium">{diasPeriodoNuevo} {diasPeriodoNuevo === 1 ? "día" : "días"}</div>
                    </div>
                  ) : (
                    <span className="text-secondary-c">—</span>
                  )}
                </td>
                <td className="text-center">
                  <span className={`np-badge ${saldo > 0 ? "np-badge-success" : saldo === 0 ? "np-badge-info" : "np-badge-warning"}`}>
                    {saldo} {saldo === 1 ? "día" : "días"}
                  </span>
                </td>
                <td className="text-center">
                  {diasVencidas > 0 ? (
                    <span className="np-badge np-badge-error">{diasVencidas} {diasVencidas === 1 ? "día" : "días"}</span>
                  ) : (
                    <span className="text-secondary-c">—</span>
                  )}
                </td>
                <td className="text-secondary-c">{proximoAniversario ? fmtFecha(proximoAniversario) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="np-card p-0">
        <div className="px-5 py-4 flex items-center justify-between border-b border-c">
          <div className="flex items-center gap-2"><Calendar size={16} className="text-primary-color" /><span className="font-medium">Historial de vacaciones</span></div>
          <span className="text-xs text-secondary-c">{registros.length} registro(s)</span>
        </div>
        <div className="np-scroll">
        <table className="np-table text-sm">
          <thead><tr>
            <th>Empleado</th>
            <th>Desde</th>
            <th>Hasta</th>
            <th className="text-center">Días</th>
            <th className="text-center">Tipo</th>
            <th></th>
          </tr></thead>
          <tbody>
            {registros.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-secondary-c">Sin vacaciones registradas. Usá el botón &quot;Registrar vacaciones&quot; para añadir un período.</td></tr>
            )}
            {registros.map(g => {
              const e = empleados.find(x => x.id === g.empleado_id);
              const esPagadas = g.tipo === "Pagadas";
              return (
                <tr key={`${g.empleado_id}-${g.fecha_inicio}-${g.tipo}-${g.asistencia_id || "r"}`}>
                  <td>{e ? `${e.nombre} ${e.apellidos}` : g.empleado_id}</td>
                  <td>{fmtFecha(g.fecha_inicio)}{esPagadas && <span className="text-secondary-c text-xs ml-1">(pago)</span>}</td>
                  <td>{esPagadas ? <span className="text-secondary-c">—</span> : fmtFecha(g.fecha_fin)}</td>
                  <td className="text-center font-medium">{g.dias}</td>
                  <td className="text-center">
                    <span className={`np-badge ${esPagadas ? "np-badge-warning" : "np-badge-success"}`} data-testid={`vac-tipo-${g.empleado_id}-${g.fecha_inicio}`}>
                      {g.tipo}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      {esPagadas && (
                        <button
                          onClick={() => descargarComprobantePagadas(g)}
                          className="np-btn np-btn-ghost p-1.5 text-primary-color"
                          title="Descargar comprobante"
                          data-testid={`vac-pdf-${g.empleado_id}-${g.fecha_inicio}`}
                        >
                          <FileText size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => setEditingGrupo(g)}
                        className="np-btn np-btn-ghost p-1.5 text-secondary-c hover:text-primary-c"
                        title="Editar registro"
                        data-testid={`vac-edit-${g.empleado_id}-${g.fecha_inicio}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => eliminarRango(g)} className="np-btn np-btn-ghost p-1.5 text-error" title="Eliminar registro" data-testid={`vac-del-${g.empleado_id}-${g.fecha_inicio}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {showForm && <FormularioVacaciones empleados={empleados} empresa={empresa} onClose={() => setShowForm(false)} onSaved={async () => { setShowForm(false); await cargar(); }} />}
      {editingGrupo && (
        <FormularioVacaciones
          empleados={empleados}
          empresa={empresa}
          asistencia={asistencia}
          editingGrupo={editingGrupo}
          onClose={() => setEditingGrupo(null)}
          onSaved={async () => { setEditingGrupo(null); await cargar(); }}
        />
      )}
    </Layout>
  );
}

function plusOneDay(iso) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return toLocalISO(d);
}

function FormularioVacaciones({ empleados, empresa = {}, asistencia = [], editingGrupo = null, onClose, onSaved }) {
  const isEditing = !!editingGrupo;
  // Encontrar el registro original si estamos editando Pagadas
  const registroPagadasOriginal = useMemo(() => (
    isEditing && editingGrupo.tipo === "Pagadas" && editingGrupo.asistencia_id
      ? asistencia.find(a => a.id === editingGrupo.asistencia_id)
      : null
  ), [isEditing, editingGrupo, asistencia]);

  const [empleadoId, setEmpleadoId] = useState(
    isEditing ? editingGrupo.empleado_id : (empleados.find(e => e.estado === "Activo")?.id || "")
  );
  const [fechaInicio, setFechaInicio] = useState(
    isEditing && editingGrupo.tipo === "Disfrutadas" ? editingGrupo.fecha_inicio : ""
  );
  const [fechaFin, setFechaFin] = useState(
    isEditing && editingGrupo.tipo === "Disfrutadas" ? editingGrupo.fecha_fin : ""
  );
  const [motivo, setMotivo] = useState(
    isEditing ? (registroPagadasOriginal?.motivo || "") : ""
  );
  const [tipo, setTipo] = useState(isEditing ? editingGrupo.tipo : "Disfrutadas");
  const [fechaPago, setFechaPago] = useState(
    isEditing && editingGrupo.tipo === "Pagadas" ? editingGrupo.fecha_inicio : ""
  );
  const [diasPagados, setDiasPagados] = useState(
    isEditing && editingGrupo.tipo === "Pagadas" ? editingGrupo.dias : 1
  );
  const [monto, setMonto] = useState(
    isEditing && editingGrupo.tipo === "Pagadas" ? String(registroPagadasOriginal?.monto_pagado ?? "") : ""
  );
  const [montoTouched, setMontoTouched] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const esPagadas = tipo === "Pagadas";
  const dias = esPagadas
    ? (Number(diasPagados) > 0 ? Number(diasPagados) : 0)
    : (fechaInicio && fechaFin ? diasEntre(fechaInicio, fechaFin) : 0);
  const empleado = empleados.find(e => e.id === empleadoId);
  const fechaMin = empleado?.fecha_ingreso;

  // Sugerencia de monto = salario diario × días (mismo cálculo que en el modal desglose)
  const salarioDiarioSugerido = useMemo(() => {
    if (!empleado) return 0;
    const salario = Number(empleado.salario_base) || 0;
    const grupoAct = empresa?.actividad_grupo;
    const freq = String(empleado.frecuencia_pago || "Mensual").toLowerCase();
    const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
    return salario / diasBase;
  }, [empleado, empresa]);
  const montoSugerido = useMemo(
    () => Math.round(salarioDiarioSugerido * (Number(diasPagados) || 0) * 100) / 100,
    [salarioDiarioSugerido, diasPagados]
  );
  // Auto-rellenar el monto sugerido si el usuario no lo tocó
  useEffect(() => {
    if (esPagadas && !montoTouched && montoSugerido > 0) {
      setMonto(String(montoSugerido));
    }
  }, [esPagadas, montoSugerido, montoTouched]);

  const submit = async (e) => {
    e.preventDefault();
    if (!empleadoId) { toast.error("Seleccioná un empleado"); return; }
    if (esPagadas) {
      if (!fechaPago) { toast.error("Ingresá la fecha del pago"); return; }
      if (!(Number(diasPagados) > 0)) { toast.error("Los días pagados deben ser mayor a 0"); return; }
      if (!(Number(monto) > 0)) { toast.error("Ingresá el monto pagado"); return; }
    } else {
      if (!fechaInicio || !fechaFin) { toast.error("Completá ambas fechas"); return; }
      if (dias <= 0) { toast.error("El rango de fechas es inválido"); return; }
    }
    setSaving(true);
    try {
      // === Modo edición: primero borrar los registros originales del grupo ===
      if (isEditing) {
        if (editingGrupo.tipo === "Pagadas" && editingGrupo.asistencia_id) {
          await api.delete(`/asistencia/${editingGrupo.asistencia_id}`).catch(() => {});
          // Si cambió la fecha del pago, borrar comprobante viejo (el nuevo se creará al re-archivar).
          if (fechaPago !== editingGrupo.fecha_inicio) {
            try {
              const { data: comps } = await api.get(`/comprobantes?empleado_id=${empleadoId}`);
              const viejo = (comps || []).find(c => c.kind === "vacaciones_pagadas" && c.fecha_inicio === editingGrupo.fecha_inicio);
              if (viejo) await api.delete(`/comprobantes/${viejo.id}`).catch(() => {});
            } catch (delErr) { console.error("No se pudo limpiar comprobante viejo:", delErr); }
          }
        } else {
          // Disfrutadas: borrar todos los días del rango original
          const fechasOriginales = fechasEnRango(editingGrupo.fecha_inicio, editingGrupo.fecha_fin);
          const toDelete = asistencia.filter(a =>
            a.empleado_id === editingGrupo.empleado_id &&
            a.estado === "Vacaciones" &&
            (a.tipo_vacacion || "Disfrutadas") !== "Pagadas" &&
            fechasOriginales.includes(a.fecha)
          );
          await Promise.all(toDelete.map(r => api.delete(`/asistencia/${r.id}`).catch(() => {})));
        }
      }

      if (esPagadas) {
        // Un solo registro con dias_pagados = N + monto_pagado
        try {
          await api.post("/asistencia", {
            empleado_id: empleadoId,
            fecha: fechaPago,
            entrada: "",
            salida: "",
            almuerzo_h: 0,
            estado: "Vacaciones",
            tipo_vacacion: "Pagadas",
            dias_pagados: Number(diasPagados),
            monto_pagado: Number(monto),
            motivo: motivo || "",
          });
          toast.success(isEditing
            ? `Pago actualizado (${diasPagados} ${Number(diasPagados) === 1 ? "día" : "días"}).`
            : `Pago registrado (${diasPagados} ${Number(diasPagados) === 1 ? "día" : "días"}). Generando comprobante…`);
          // Generar comprobante PDF + archivar en el expediente del trabajador
          try {
            const result = generarComprobanteVacacionesPagadas({
              empresa, empleado, fechaPago,
              diasPagados: Number(diasPagados),
              monto: Number(monto),
              motivo: motivo || "",
            });
            await api.post("/comprobantes", {
              empleado_id: empleadoId,
              periodo_label: `Vacaciones Pagadas ${fmtFecha(fechaPago)}`,
              kind: "vacaciones_pagadas",
              fecha_inicio: fechaPago,
              fecha_fin: fechaPago,
              monto_bruto: Number(monto),
              monto_ccss: 0,
              monto_rebajos: 0,
              monto_neto: Number(monto),
              file_name: result.fileName,
              pdf_base64: result.pdfBase64,
            }).catch(err => console.error("No se pudo archivar el comprobante:", err));
          } catch (pdfErr) {
            toast.warning("El pago se guardó pero el comprobante falló: " + (pdfErr?.message || ""));
          }
        } catch (err) {
          const msg = err?.response?.data?.detail || err?.message || "Error desconocido";
          toast.error(`No se pudo guardar: ${msg}`);
        }
      } else {
        let ok = 0;
        const errores = [];
        const fechas = fechasEnRango(fechaInicio, fechaFin);
        for (const f of fechas) {
          try {
            await api.post("/asistencia", {
              empleado_id: empleadoId,
              fecha: f,
              entrada: "",
              salida: "",
              almuerzo_h: 0,
              estado: "Vacaciones",
              tipo_vacacion: "Disfrutadas",
              motivo: motivo || "",
            });
            ok++;
          } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Error desconocido";
            errores.push(`${f}: ${msg}`);
          }
        }
        if (errores.length === 0) {
          toast.success(isEditing
            ? `Vacaciones actualizadas (${ok} ${ok === 1 ? "día" : "días"}).`
            : `Vacaciones registradas (${ok} ${ok === 1 ? "día" : "días"}).`);
        } else if (ok > 0) {
          toast.warning(`Se guardaron ${ok} de ${fechas.length} días. Errores: ${errores.slice(0, 2).join("; ")}`);
        } else {
          toast.error(`No se pudo guardar ningún día. ${errores.slice(0, 2).join("; ")}`);
        }
      }
      await onSaved();
    } catch (err) {
      toast.error("Error inesperado: " + (err?.message || "revisá la consola"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
        <div className="p-5 border-b border-c flex items-center justify-between">
          <h3 className="font-display text-xl">{isEditing ? "Editar vacaciones" : "Registrar vacaciones"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#FAFAF7]" data-testid="close-vac-form"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">Empleado</label>
            <select value={empleadoId} onChange={e => setEmpleadoId(e.target.value)} disabled={isEditing} className="np-input w-full disabled:opacity-60 disabled:cursor-not-allowed" data-testid="vac-empleado">
              {empleados.filter(e => e.estado === "Activo" || e.id === empleadoId).map(e => (
                <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {esPagadas ? (
              <>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">Fecha de pago</label>
                  <input type="date" value={fechaPago} onChange={e => setFechaPago(e.target.value)} min={fechaMin} required className="np-input w-full" data-testid="vac-fecha-pago" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">Días pagados</label>
                  <input type="number" min="1" step="1" value={diasPagados} onChange={e => setDiasPagados(e.target.value)} required className="np-input w-full" data-testid="vac-dias-pagados" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">
                    Monto pagado (₡)
                    {salarioDiarioSugerido > 0 && (
                      <span className="ml-2 text-secondary-c font-normal normal-case tracking-normal">
                        · sugerido: ₡{new Intl.NumberFormat("es-CR", { minimumFractionDigits: 2 }).format(montoSugerido)}
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={monto}
                    onChange={e => { setMonto(e.target.value); setMontoTouched(true); }}
                    required
                    className="np-input w-full"
                    placeholder="0.00"
                    data-testid="vac-monto"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">Desde</label>
                  <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} min={fechaMin} required className="np-input w-full" data-testid="vac-inicio" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">Hasta</label>
                  <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} min={fechaInicio || fechaMin} required className="np-input w-full" data-testid="vac-fin" />
                </div>
              </>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">Tipo de vacaciones</label>
            <div className="grid grid-cols-2 gap-2" data-testid="vac-tipo-selector">
              {[
                { v: "Disfrutadas", desc: "El empleado tomará los días libres" },
                { v: "Pagadas", desc: "Se le pagan en lugar de disfrutarlas" },
              ].map(opt => {
                const active = tipo === opt.v;
                const disabled = isEditing;
                return (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => !disabled && setTipo(opt.v)}
                    disabled={disabled}
                    className={`text-left p-3 rounded-xl border transition-all ${active ? "bg-[#E6EFEA] border-[#274735]" : "bg-white border-c hover:border-[#274735]/40"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`vac-tipo-${opt.v.toLowerCase()}`}
                  >
                    <div className={`text-sm font-medium ${active ? "text-primary-color" : "text-primary-c"}`}>{opt.v}</div>
                    <div className="text-[11px] text-secondary-c mt-0.5">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-secondary-c mb-1.5">Motivo / Observación (opcional)</label>
            <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} className="np-input w-full" placeholder="Vacaciones programadas, etc." />
          </div>
          {dias > 0 && (
            <div className="p-3 rounded-xl bg-[#FAFAF7] border border-c text-sm">
              {esPagadas
                ? <>Se rebajarán <strong>{dias} {dias === 1 ? "día" : "días"}</strong> del saldo como <strong>Pagadas</strong> con fecha {fechaPago ? fmtFecha(fechaPago) : "—"}.</>
                : <>Se registrarán <strong>{dias} {dias === 1 ? "día" : "días"}</strong> como <strong>Disfrutadas</strong> (se rebajan del saldo).</>}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="np-btn np-btn-ghost">Cancelar</button>
            <button type="submit" disabled={saving || dias <= 0} className="np-btn np-btn-primary" data-testid="vac-save">{saving ? "Guardando…" : (isEditing ? "Actualizar" : "Guardar")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
